import axios from 'axios';
import Project from '../../common/Utils/project';
import { generateGuid } from '../../common/Utils/guid';
import { useAuth } from 'oidc-react';
import { useEffect } from 'react';
import { useToast } from '../../providers/ToastProvider/ToastContext';

let tokenGetter: (() => string | undefined) | null = null;
let addToastGlobal:
  | ((
      msg: string,
      type?: any,
      duration?: number,
      title?: string,
      copyable?: boolean,
      meta?: any,
      correlationId?: string,
      notify?: boolean,
    ) => void)
  | null = null;

// 🔹 Allow toast context registration once provider is ready
export const registerGlobalToast = (toastFn: typeof addToastGlobal) => {
  addToastGlobal = toastFn;
};

// 🔹 Function to set token getter from AuthGate
export const setTokenGetter = (getter: () => string | undefined) => {
  tokenGetter = getter;
};

// 🔹 Base axios instance (shared globally)
const api = axios.create({
  baseURL: Project.api,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 🔹 Request interceptor
api.interceptors.request.use((config) => {
  const token = tokenGetter ? tokenGetter() : undefined;
  const correlationId = generateGuid();

  config.headers['Correlation-Id'] = correlationId;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // keep correlation ID in metadata for later toast
  (config as any).metadata = { correlationId };
  return config;
});

// 🔹 Response interceptor
api.interceptors.response.use(
  (response) => {
    const correlationId = (response.config as any)?.metadata?.correlationId;
    if (correlationId) (response as any).correlationId = correlationId;
    return response;
  },
  (error) => {
    const correlationId = (error.config as any)?.metadata?.correlationId;
    const message =
      error?.response?.data?.message || error?.response?.data?.title || 'An unexpected error occurred while processing the request.';

    if (addToastGlobal) {
      // pass correlationId and notify=true so error toasts are saved to notifications
      addToastGlobal(message, 'error', 6000, 'Request Failed', false, undefined, correlationId, true);
    }

    console.error(
      `[API ERROR] → ${error.config?.url || 'Unknown endpoint'} | Correlation ID: ${correlationId}`,
      error?.response?.data || error,
    );

    return Promise.reject(error);
  },
);

export default api;

// 🔹 Request config that bypasses the browser HTTP cache. Some APIs answer server-cache hits with
// `Cache-Control: max-age=<server TTL>`, so after a write the browser would keep serving the old response.
export const noBrowserCache = () => ({ params: { _ts: Date.now() } });

// 🔹 Hook for components that use Auth + Toast
export const useApi = () => {
  const auth = useAuth();
  const { addToast } = useToast();
  const token = auth?.userData?.access_token;

  useEffect(() => {
    setTokenGetter(() => token);
  }, [token]);

  // register toast globally once
  useEffect(() => {
    if (addToast) registerGlobalToast(addToast);
  }, [addToast]);

  return api;
};
